import { escapeHtml } from '../utils/escapeHtml';
import type { ChessPlayerColor } from '../utils/chessPgnMeta';

export type ChessAnalyzeGameInput = {
  pgn: string;
  playerColor: ChessPlayerColor | null;
};

let resultModalEl: HTMLElement | null = null;
let resultBodyEl: HTMLElement | null = null;
let resultTitleEl: HTMLElement | null = null;

let analyzeModalEl: HTMLElement | null = null;

function closeResultModal(): void {
  if (!resultModalEl) return;
  resultModalEl.classList.add('hidden');
  document.body.style.overflow = '';
}

function ensureResultModal(): HTMLElement {
  if (resultModalEl) return resultModalEl;

  const modal = document.createElement('div');
  modal.id = 'chess-coach-result-modal';
  modal.className = 'chess-coach-modal hidden';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.innerHTML = `
    <div class="chess-coach-modal-backdrop" aria-hidden="true"></div>
    <div class="chess-coach-modal-sheet">
      <header class="chess-coach-modal-header">
        <h2 class="chess-coach-modal-title" id="chess-coach-result-title">Chess coach</h2>
        <button type="button" class="btn-primary btn-sm" id="chess-coach-result-close">Close</button>
      </header>
      <div class="chess-coach-modal-body" id="chess-coach-result-body" aria-live="polite"></div>
    </div>
  `;

  resultTitleEl = modal.querySelector('#chess-coach-result-title');
  resultBodyEl = modal.querySelector('#chess-coach-result-body');
  modal.querySelector('.chess-coach-modal-backdrop')!.addEventListener('click', closeResultModal);
  modal.querySelector('#chess-coach-result-close')!.addEventListener('click', closeResultModal);

  document.body.appendChild(modal);
  resultModalEl = modal;
  return modal;
}

export function mountChessCoachModals(): void {
  ensureResultModal();
  ensureAnalyzeModal();
}

export function openChessCoachResultModal(title: string, bodyHtml: string): void {
  const modal = ensureResultModal();
  if (resultTitleEl) resultTitleEl.textContent = title;
  if (resultBodyEl) resultBodyEl.innerHTML = bodyHtml;
  modal.classList.remove('hidden');
  document.body.style.overflow = 'hidden';
}

export function setChessCoachResultBody(bodyHtml: string): void {
  if (resultBodyEl) resultBodyEl.innerHTML = bodyHtml;
}

export function coachLoadingHtml(message: string): string {
  return `<p class="chess-coach-loading">${escapeHtml(message)}</p>`;
}

export function coachReplyHtml(text: string, footerHtml?: string): string {
  const safe = escapeHtml(text).replace(/\n/g, '<br>');
  const footer = footerHtml ? `<p class="chess-coach-reply-footer">${footerHtml}</p>` : '';
  return `<div class="chess-coach-reply">${safe}</div>${footer}`;
}

function closeAnalyzeModal(): void {
  if (!analyzeModalEl) return;
  analyzeModalEl.classList.add('hidden');
  document.body.style.overflow = '';
}

function ensureAnalyzeModal(): HTMLElement {
  if (analyzeModalEl) return analyzeModalEl;

  const modal = document.createElement('div');
  modal.id = 'chess-analyze-game-modal';
  modal.className = 'chess-coach-modal hidden';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'Analyze game');
  modal.innerHTML = `
    <div class="chess-coach-modal-backdrop" aria-hidden="true"></div>
    <div class="chess-coach-modal-sheet chess-coach-modal-sheet--form">
      <header class="chess-coach-modal-header">
        <h2 class="chess-coach-modal-title">Analyze game</h2>
        <button type="button" class="btn-ghost-dark btn-sm" id="chess-analyze-cancel">Cancel</button>
      </header>
      <p class="chess-coach-modal-hint">Paste a PGN from Lichess or another site. Uses your Cursor API key from Tools → Config (same as AI Agent).</p>
      <form id="chess-analyze-form">
        <label class="chess-coach-label" for="chess-analyze-color">You played</label>
        <select id="chess-analyze-color" class="chess-coach-select">
          <option value="">Not specified</option>
          <option value="white">White</option>
          <option value="black">Black</option>
        </select>
        <label class="chess-coach-label" for="chess-analyze-pgn">PGN</label>
        <textarea
          id="chess-analyze-pgn"
          class="chess-coach-textarea"
          rows="12"
          spellcheck="false"
          placeholder="[Event &quot;Casual&quot;]&#10;[Site &quot;lichess.org&quot;]&#10;..."
        ></textarea>
        <p class="chess-coach-form-error hidden" id="chess-analyze-error" role="alert"></p>
        <div class="chess-coach-form-actions">
          <button type="submit" class="btn-primary" id="chess-analyze-submit">Analyze with coach</button>
        </div>
      </form>
    </div>
  `;

  document.body.appendChild(modal);
  analyzeModalEl = modal;
  return modal;
}

/** Modal to paste a PGN; resolves with input or null if cancelled. */
export function promptChessAnalyzeGame(): Promise<ChessAnalyzeGameInput | null> {
  const modal = ensureAnalyzeModal();
  const form = modal.querySelector<HTMLFormElement>('#chess-analyze-form')!;
  const textarea = modal.querySelector<HTMLTextAreaElement>('#chess-analyze-pgn')!;
  const colorSelect = modal.querySelector<HTMLSelectElement>('#chess-analyze-color')!;
  const errorEl = modal.querySelector<HTMLElement>('#chess-analyze-error')!;

  return new Promise(resolve => {
    let settled = false;
    const finish = (input: ChessAnalyzeGameInput | null) => {
      if (settled) return;
      settled = true;
      closeAnalyzeModal();
      form.removeEventListener('submit', onSubmit);
      modal.querySelector('#chess-analyze-cancel')!.removeEventListener('click', onCancel);
      resolve(input);
    };

    const onCancel = () => finish(null);
    const onSubmit = (e: Event) => {
      e.preventDefault();
      const pgn = textarea.value.trim();
      if (!pgn) {
        errorEl.textContent = 'Paste a PGN first.';
        errorEl.classList.remove('hidden');
        return;
      }
      errorEl.classList.add('hidden');
      const colorVal = colorSelect.value;
      const playerColor: ChessPlayerColor | null =
        colorVal === 'white' || colorVal === 'black' ? colorVal : null;
      finish({ pgn, playerColor });
    };

    textarea.value = '';
    colorSelect.value = '';
    errorEl.classList.add('hidden');
    errorEl.textContent = '';
    form.addEventListener('submit', onSubmit);
    modal.querySelector('#chess-analyze-cancel')!.addEventListener('click', onCancel);
    modal.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    textarea.focus();
  });
}
